import React, { useEffect, useState } from "react";
import ReviewService from "../../../services/ReviewService";
import PkgService from "../../../services/PkgService";
import { SubmitHandler, useForm } from "react-hook-form";
import httpUtil from "../../../utils/http.util";
import {
  MsgError,
  MsgLoading,
  MsgSuccess,
} from "../../../components/Loaders/MessageLoaders";

interface IReviewCategory {
  id: string;
  review_category_name: string;
  review_category_img: string;
}

export interface ReviewFormInputType {
  rating: number;
  name: string;
  reviewer_img: FileList;
  address: string;
  review_category: string;
  pkg: string;
  review_headline: string;
  message: string;
}
const TabAddReview = ({
  editReviewWithId,
  fetchReview,
}: {
  editReviewWithId?: string;
  fetchReview?: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<IReviewCategory[]>([]);
  const [pkg, setPkg] = useState([]);

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<ReviewFormInputType>();

  const fetchCategoris = async () => {
    try {
      const res = await ReviewService.fetchCategories();
      setCategories(res.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPkgs = async () => {
    try {
      const res = await PkgService.AllPkges();
      setPkg(res.data.data[0]);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchReviewDetail = async (id: string) => {
    try {
      const res = await ReviewService.fetchDetails(id);
      setValue("review_category", res.data.data.review_category.id);
      setValue("address", res.data.data.address);
      setValue("message", res.data.data.message);
      setValue("name", res.data.data.name);
      setValue("rating", res.data.data.rating);
      setValue("review_headline", res.data.data.review_headline);
      setValue("pkg", res.data.data.pkg.id);
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit: SubmitHandler<ReviewFormInputType> = async (data) => {
    try {
      MsgLoading();
      const formattedData = { ...data };
      formattedData["reviewer_img"] = data.reviewer_img[0] as any;

      if (editReviewWithId && fetchReview) {
        updateReview(editReviewWithId, data, fetchReview);
      } else {
        const res = await ReviewService.createReview(formattedData);
        MsgSuccess(res.data.message);
        reset();
      }
    } catch (error: any) {
      if (error.response) {
        MsgError(error.response.data.message);
      }
      console.log(error);
    }
  };

  const updateReview = async (
    id: string,
    data: ReviewFormInputType,
    fetchReview: () => void
  ) => {
    try {
      MsgLoading();
      const res = await ReviewService.updateReview(id, data);
      fetchReview();
      MsgSuccess(res.data.message);
    } catch (error: any) {
      console.log(error);
      if (error.response) {
        MsgError(error.response.data.message || "Error while updating item.");
      }
    }
  };

  useEffect(() => {
    fetchCategoris();
    fetchPkgs();
    if (editReviewWithId) {
      fetchReviewDetail(editReviewWithId);
    }
  }, [editReviewWithId]);

  return (
    <div>
      <form action="" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex flex-col space-y-2 ">
            <label htmlFor="category">Review category </label>
            <select
              id="category"
              className="form-select inputs"
              {...register("review_category", { required: true })}
            >
              <option value="">--select--</option>
              {categories.map((item, i) => (
                <option value={item.id} key={i}>
                  {item.review_category_name}
                </option>
              ))}
            </select>
            {errors.review_category && (
              <span className="text-red-500">
                {errors.review_category.message || "This field is required!"}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2 ">
            <label htmlFor="pkg">Review package </label>
            <select
              id="pkg"
              className="form-select inputs"
              {...register("pkg", { required: true })}
            >
              <option value="">--select--</option>
              {pkg.map((item: any, i) => (
                <option value={item.id} key={i}>
                  {item.title}
                </option>
              ))}
            </select>
            {errors.review_category && (
              <span className="text-red-500">
                {errors.review_category.message || "This field is required!"}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2 ">
            <label htmlFor="name">Review's name </label>
            <input
              type="text"
              id="name"
              className="form-input inputs"
              {...register("name", { required: true })}
            />
            {errors.name && (
              <span className="text-red-500">
                {errors.name.message || "This field is required!"}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2 ">
            <label htmlFor="address">Address </label>
            <input
              type="text"
              id="address"
              className="form-input inputs"
              {...register("address", { required: true })}
            />
            {errors.address && (
              <span className="text-red-500">
                {errors.address.message || "This field is required!"}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2 ">
            <label htmlFor="img">Image </label>
            <input
              type="file"
              id="img"
              className="form-input inputs"
              {...register("reviewer_img", { required: false })}
            />
            {errors.reviewer_img && (
              <span className="text-red-500">
                {errors.reviewer_img.message || "This field is required!"}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <label htmlFor="rating">Rating (0-5) </label>
            <select
              className="form-input inputs"
              id="rating"
              {...register("rating", {
                required: true,
              })}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
            {errors.rating && (
              <span className="text-red-500">
                {errors.rating.message || "This field is required!"}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col space-y-2 my-3">
          <label htmlFor="review_headline">Review Headline </label>
          <input
            type="text"
            id="review_headline"
            className="form-input inputs"
            {...register("review_headline", {
              required: true,
            })}
          />
          {errors.review_headline && (
            <span className="text-red-500">
              {errors.review_headline.message || "This field is required!"}
            </span>
          )}
        </div>

        <div className="flex flex-col space-y-2 mb-3">
          <label htmlFor="message">Message </label>
          <textarea
            id="message"
            rows={5}
            className="form-input inputs"
            {...register("message", {
              required: true,
            })}
          />
          {errors.message && (
            <span className="text-red-500">
              {errors.message.message || "This field is required!"}
            </span>
          )}
        </div>

        <div className="my-4">
          <input
            type="submit"
            value={loading ? "Loading" : "Submit"}
            disabled={loading}
            className="btn btn-primary"
          />
        </div>
      </form>
    </div>
  );
};

export default TabAddReview;
